import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getGroupUsers } from 'core/crud/groups.crud';
import { useGroups } from 'core/hooks/useGroups';
import * as coreEntities from 'core/store/index';
import { parseUsers } from 'core/store/parsers';
import { filterStudents } from 'core/utils/user';

export type StudentInfo = {
  avatar: string | null;
  guid: string;
  groupName: string;
  lastName: string;
  name: string;
};

type GroupEntry = {
  guid: string;
  name: string;
};

type UseStudentsMapReturn = {
  loading: boolean;
  studentsMap: Map<string, StudentInfo>;
};

async function fetchStudentsBySchool(schoolGuid: string, groupEntries: GroupEntry[]): Promise<Map<string, StudentInfo>> {
  const map = new Map<string, StudentInfo>();

  const promises = groupEntries.map((entry) => getGroupUsers({ schoolGuid, groupGuid: entry.guid }));
  const responses = await Promise.all(promises);

  responses.forEach((response: any, index: number) => {
    const groupName = groupEntries[index].name;
    const users = parseUsers(response?.data?.data?.users || []);
    const students = filterStudents(users);

    students.forEach((student: any) => {
      if (student.guid && !map.has(student.guid)) {
        map.set(student.guid, {
          avatar: student.avatar || null,
          guid: student.guid,
          groupName,
          lastName: student.lastName || student.lastname || '',
          name: student.name || '',
        });
      }
    });
  });

  return map;
}

function useStudentsMap(): UseStudentsMapReturn {
  const { groups } = useGroups();
  const groupsLoading = useSelector((state: unknown) => coreEntities.groups.selectors.getGroupsLoading(state));
  const user = useSelector((state: unknown) => coreEntities.auth.selectors.getUser(state));

  const [studentsMap, setStudentsMap] = useState<Map<string, StudentInfo>>(new Map());
  const [fetching, setFetching] = useState<boolean>(false);

  const schoolGuid = (user as any)?.schools?.[0]?.guid || null;

  const groupEntries = useMemo((): GroupEntry[] => {
    const schoolGroups: GroupEntry[] = groups?.schoolGroups || [];
    return schoolGroups.map((g) => ({
      guid: g.guid,
      name: g.name,
    }));
  }, [groups]);

  useEffect(() => {
    if (!schoolGuid || groupEntries.length === 0) return undefined;

    let cancelled = false;
    setFetching(true);

    fetchStudentsBySchool(schoolGuid, groupEntries)
      .then((map) => {
        if (cancelled) return;
        setStudentsMap(map);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useStudentsMap] Error fetching group users:', err);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolGuid, groupEntries]);

  return { loading: !!groupsLoading || fetching, studentsMap };
}

export default useStudentsMap;
